import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { GameLaunchMode } from '../../constants/gameLaunchMode';
import { GamePopup } from '../../components/gamePopup';
import { buildAccessLinkPath, getGameLaunchMode } from '../../helpers/gameLaunch';
import { useGameLaunch } from '../../hooks/useGameLaunch';
import { gamesService } from '../../services/games';

import styles from './GameLauncher.module.css';

type LaunchStatus = 'loading' | 'launching' | 'not-found';

export function GameLauncher() {
    const { id, gameSystemName } = useParams<{ id: string; gameSystemName: string }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<LaunchStatus>('loading');
    const launchAttempted = useRef(false);

    const accessLinkPath = id ? buildAccessLinkPath(id) : '/';
    const catalogUrl = useMemo(
        () => new URL(accessLinkPath, window.location.origin).href,
        [accessLinkPath]
    );

    const { handleLaunch, activeGame, handleClose } = useGameLaunch({
        returnUrl: catalogUrl,
        onAfterClose: () => navigate(accessLinkPath, { replace: true }),
    });

    useEffect(() => {
        if (!gameSystemName || launchAttempted.current) {
            return;
        }

        let isMounted = true;

        gamesService
            .getLiveGames()
            .then((games) => {
                if (!isMounted) {
                    return;
                }

                const game = games.find((item) => item.id === gameSystemName);

                if (!game || game.comingSoon) {
                    setStatus('not-found');
                    return;
                }

                launchAttempted.current = true;
                setStatus('launching');
                const launchMode = getGameLaunchMode();
                void handleLaunch(game).then(() => {
                    if (launchMode === GameLaunchMode.NewTab) {
                        window.location.replace(accessLinkPath);
                    }
                });
            })
            .catch(() => {
                if (isMounted) {
                    setStatus('not-found');
                }
            });

        return () => {
            isMounted = false;
        };
    }, [gameSystemName, handleLaunch, accessLinkPath]);

    if (status === 'not-found') {
        return <Navigate to={accessLinkPath} replace />;
    }

    return (
        <div className={styles.container}>
            {status === 'loading' ? <p className={styles.message}>Loading game...</p> : null}
            {activeGame ? (
                <GamePopup
                    url={activeGame.url}
                    name={activeGame.name}
                    format={activeGame.format}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
}
